import * as React from 'react';
import Dropdown, { Option } from '@app/react-common/components/dropdown/Dropdown';
import { PercentInput } from '@app/react-common/components/percent-input/PercentInput';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import {
  TrafficAllocationVariant,
  TrafficAllocationWidgetSelection,
} from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import {
  ExtensionsTypeToDisplayName,
  ICheckoutExtensions,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import {
  AbTestFormData,
  formatSelectedExtensionNames,
  haveVariantsWithIdenticalExtensionSets,
} from '../shared/abTestModalUtils';

const CONTROL_GROUP_SLOT_INDEX = 0;
const TEST_GROUP_SLOT_INDEX = 1;

interface ExtensionsAllocationSectionProps {
  formData: AbTestFormData;
  setFormData: (data: any) => void;
  setTrafficAllocationValid: (valid: boolean) => void;
  extensions: ICheckoutExtensions[];
  mode: string;
}

export const ExtensionsAllocationSection: React.FC<ExtensionsAllocationSectionProps> = ({
  formData,
  setFormData,
  setTrafficAllocationValid,
  extensions,
  mode,
}) => {
  const viewMode = mode === 'view';
  const variants = formData?.abTestVariants ?? [];

  const selectableExtensions = (extensions ?? []).filter((extension) => extension.type !== 'datePicker');

  const buildExtensionOptionsForSlot = (slotIndex: number): Option[] =>
    selectableExtensions.map((extension) => ({
      label: extension.template?.internalName || 'Extension',
      value: extension.id,
      customContent: (
        <div
          className="traffic-allocation-dropdown__option"
          onClick={() => toggleExtensionInSlot(slotIndex, extension.id)}>
          <span className="traffic-allocation-dropdown__option__name">
            {extension.template?.internalName || 'Extension'}
          </span>
          <span className="traffic-allocation-dropdown__option__group-type">
            {ExtensionsTypeToDisplayName[extension.type]}
          </span>
        </div>
      ),
    }));

  const hasIdenticalExtensionSets = haveVariantsWithIdenticalExtensionSets(variants);

  const selectedExtensionIds = variants.flatMap((variant) =>
    (variant.widgetSelections ?? []).map((selection) => selection.widgetExternalId),
  );
  const selectedExtensionTypes = new Set(
    (extensions ?? [])
      .filter((extension) => selectedExtensionIds.includes(extension.id))
      .map((extension) => extension.type),
  );

  const percentageSum = variants.reduce((sum, variant) => sum + (variant.percentageTraffic || 0), 0);

  const updateVariantAtSlot = (
    slotIndex: number,
    updateVariant: (variant: TrafficAllocationVariant) => TrafficAllocationVariant,
  ) => {
    setFormData((previousFormData: AbTestFormData) => ({
      ...previousFormData,
      abTestVariants: previousFormData.abTestVariants.map((variant) =>
        variant.slotIndex === slotIndex ? updateVariant(variant) : variant,
      ),
    }));
  };

  const setSlotExtensionIds = (slotIndex: number, selectedExtensionIdsOfSlot: number[]) => {
    updateVariantAtSlot(slotIndex, (variant) => {
      const existingSelections = variant.widgetSelections ?? [];
      const widgetSelections: TrafficAllocationWidgetSelection[] = selectedExtensionIdsOfSlot.map((extensionId) => ({
        widgetExternalId: extensionId,
        widgetApiId: existingSelections.find((selection) => selection.widgetExternalId === extensionId)?.widgetApiId,
      }));
      return { ...variant, widgetSelections };
    });
  };

  const toggleExtensionInSlot = (slotIndex: number, extensionId: number) => {
    if (viewMode) return;
    const variant = variants.find((v) => v.slotIndex === slotIndex);
    const currentExtensionIds = (variant?.widgetSelections ?? []).map((selection) => selection.widgetExternalId);
    setSlotExtensionIds(
      slotIndex,
      currentExtensionIds.includes(extensionId)
        ? currentExtensionIds.filter((id) => id !== extensionId)
        : [...currentExtensionIds, extensionId],
    );
  };

  const handlePercentageChange = (slotIndex: number, percentage: number) => {
    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    setFormData((previousFormData: AbTestFormData) => ({
      ...previousFormData,
      abTestVariants: previousFormData.abTestVariants.map((variant) => ({
        ...variant,
        percentageTraffic: variant.slotIndex === slotIndex ? clampedPercentage : 100 - clampedPercentage,
      })),
    }));
  };

  React.useEffect(() => {
    setTrafficAllocationValid(variants.length === 2 && percentageSum === 100 && !hasIdenticalExtensionSets);
  }, [variants.length, percentageSum, hasIdenticalExtensionSets, setTrafficAllocationValid]);

  return (
    <div className="traffic-allocation-dropdown extensions-allocation-section">
      {selectedExtensionTypes.size > 1 && (
        <WarningBanner
          title="Pay attention"
          content={`Extensions of ${selectedExtensionTypes.size} different types are selected, so ${selectedExtensionTypes.size} Shopify blocks will be used`}
          hideCloseButton={true}
          variant="attention"
        />
      )}
      {hasIdenticalExtensionSets && variants.some((variant) => (variant.widgetSelections ?? []).length > 0) && (
        <WarningBanner
          title="Both groups hold the same extensions"
          content="The control group and the test group must differ by at least one extension, otherwise there is nothing to compare."
          hideCloseButton={true}
          variant="danger"
        />
      )}
      {[CONTROL_GROUP_SLOT_INDEX, TEST_GROUP_SLOT_INDEX].map((slotIndex) => {
        const variant = variants.find((v) => v.slotIndex === slotIndex);
        const extensionOptions = buildExtensionOptionsForSlot(slotIndex);
        const preSelectedOptions = (variant?.widgetSelections ?? [])
          .map((selection) => extensionOptions.find((option) => option.value === selection.widgetExternalId))
          .filter((option) => option !== undefined);

        return (
          <div key={slotIndex} className="traffic-allocation-dropdown__item">
            <div className="traffic-allocation-dropdown__item__label">{variant?.name}</div>
            <div className="traffic-allocation-dropdown__item__controls">
              <div className="traffic-allocation-dropdown__item__selection">
                <Dropdown
                  options={extensionOptions}
                  multiSelect={true}
                  searchable={true}
                  listMaxHeight={240}
                  showSelectUnselectOptions={true}
                  deselectAllLabel="Clear all"
                  deselectAllOnly={true}
                  formatSelectedOptionsLabel={(selectedOptions) =>
                    formatSelectedExtensionNames(selectedOptions.map((option) => option.label))
                  }
                  preSelectedOptions={preSelectedOptions}
                  onSelect={(selectedOptions) => {
                    if (!viewMode) {
                      setSlotExtensionIds(
                        slotIndex,
                        selectedOptions.map((option) => option.value),
                      );
                    }
                  }}
                  placeholder="No extensions (control)"
                  disabled={viewMode}
                />
              </div>
              <div className="traffic-allocation-dropdown__item__percentage">
                <PercentInput
                  value={variant?.percentageTraffic || 0}
                  setValue={(value) => {
                    if (!viewMode) {
                      handlePercentageChange(slotIndex, value);
                    }
                  }}
                  disabled={viewMode}
                  viewMode={viewMode}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
